import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { Button } from 'antd';
import { FaShuttleVan } from 'react-icons/fa';
import { PackageComboRowProps } from '../types/bot.types';
import { formatBotPrice } from './formatBotPrice';
import PackageFlightsInfo from './PackageFlightsInfo';

/**
 * One selectable package combination inside a hotel group.
 *
 * Combinations of the same hotel frequently share a price and differ only in
 * flight times (ALP prices a package per deal, not per flight pair), so the
 * flights lead the row and the price is repeated on EVERY row — equal prices
 * then read as equal, and the genuinely cheaper combination stands out.
 *
 * `optionNumber` is `result_index + 1`, matching the number in the chat text so
 * the user can pick by clicking or by saying "the second one".
 */
const PackageComboRow: React.FC<PackageComboRowProps> = ({
  optionNumber,
  flights,
  airline,
  mealPlan,
  nights,
  transferIncluded,
  totalPrice,
  currency,
  onSelect,
}) => {
  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const generalT = useSelector(
    (s: RootState) => (s.config.translations as any)?.general ?? {}
  );

  return (
    <div className="bot-package-combo">
      <span className="bot-package-combo__number">{optionNumber}</span>
      <div className="bot-package-combo__body">
        {flights ? (
          <PackageFlightsInfo flights={flights} compact />
        ) : (
          airline && <div className="bot-package-combo__airline">✈️ {airline}</div>
        )}
        <div className="bot-package-combo__badges">
          {mealPlan && (
            <span className="bot-result-card__badge">{mealPlan}</span>
          )}
          {/* Only passed when nights differ inside the group — otherwise the
              group header already states them. */}
          {nights !== undefined && (
            <span className="bot-result-card__badge">
              {nights} {t?.result?.nights ?? 'nights'}
            </span>
          )}
          {transferIncluded && (
            <span className="bot-result-card__badge bot-result-card__badge--green">
              <FaShuttleVan />{' '}
              {t?.result?.transferIncluded ?? 'Transfer included'}
            </span>
          )}
        </div>
      </div>
      <div className="bot-package-combo__action">
        <div className="bot-package-combo__price">
          {currency} {formatBotPrice(totalPrice)}
          <span className="bot-package-combo__price-suffix">
            {' '}
            {generalT.perPerson ?? '/person'}
          </span>
        </div>
        <Button
          size="small"
          type="primary"
          className="btn-style-5"
          onClick={onSelect}
        >
          {t?.result?.select ?? 'Select'}
        </Button>
      </div>
    </div>
  );
};

export default PackageComboRow;
